import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { db, auth } from "../firebase";
import { EduClass, EduHunt } from "../types";

export default function TeacherDashboard() {
  const navigate = useNavigate();
  const [classes, setClasses] = useState<EduClass[]>([]);
  const [hunts, setHunts] = useState<EduHunt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    return onAuthStateChanged(auth, async (u) => {
      if (!u || u.isAnonymous) {
        navigate("/teacher");
        return;
      }
      try {
        const cQ = query(
          collection(db, "edu_classes"),
          where("teacherId", "==", u.uid)
        );
        const cSnap = await getDocs(cQ);
        setClasses(
          cSnap.docs.map((d) => ({ ...(d.data() as EduClass), classId: d.id }))
        );

        const hQ = query(
          collection(db, "edu_hunts"),
          where("teacherId", "==", u.uid),
          orderBy("createdAt", "desc")
        );
        const hSnap = await getDocs(hQ);
        setHunts(
          hSnap.docs.map((d) => ({ ...(d.data() as EduHunt), huntId: d.id }))
        );
      } catch (e) {
        // Without this the hunts query (which needs the edu_hunts
        // teacherId + createdAt composite index) failed silently and the
        // page just claimed the teacher had no hunts.
        setError(e instanceof Error ? e.message : "Could not load your classes.");
      } finally {
        setLoading(false);
      }
    });
  }, [navigate]);

  if (loading) {
    return (
      <main className="screen">
        <div className="card"><p className="muted">Loading...</p></div>
      </main>
    );
  }

  return (
    <main className="screen">
      <div className="card wide">
        <header className="row between">
          <h1>Teacher Dashboard</h1>
          <button
            className="link"
            onClick={() => signOut(auth).then(() => navigate("/teacher"))}
          >
            Sign out
          </button>
        </header>

        {error && <p className="error">{error}</p>}

        <section>
          <div className="row between">
            <h2>Classes</h2>
            <Link to="/teacher/create" className="primary-btn">New hunt</Link>
          </div>
          {classes.length === 0 ? (
            <p className="muted">
              No classes yet. Create your first hunt to get a class code.
            </p>
          ) : (
            <ul className="list">
              {classes.map((c) => (
                <li key={c.classId}>
                  <strong>{c.name}</strong>
                  <code className="code">{c.joinCode}</code>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section>
          <h2>Hunts</h2>
          {hunts.length === 0 ? (
            <p className="muted">No hunts yet.</p>
          ) : (
            <ul className="list">
              {hunts.map((h) => (
                <li key={h.huntId}>
                  <Link to={`/teacher/hunt/${h.huntId}`}>
                    <strong>{h.title}</strong>{" "}
                    <span className="muted">· {h.status}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </main>
  );
}
